/**
 * Bloomerang Donation Form Enhancements
 *
 * Applies structural DOM changes that CSS alone cannot achieve:
 *   - Preset amount buttons ($100 / $200 / $500 / $1,000 / Other)
 *   - Tagline text under the Amount label
 *   - Replace short text inputs with textareas for multi-line fields
 *
 * All changes are non-destructive: original inputs remain in the DOM
 * (hidden when appropriate) so Bloomerang's own JS keeps working.
 */

const AMOUNT_PRESETS = [
	{ label: '$100', value: '100' },
	{ label: '$326', value: '326' },
	{ label: '$500', value: '500' },
	{ label: '$1,000', value: '1000' },
	{ label: 'Other', value: 'other' },
];

const DONATION_TAGLINE = 'Just $326 funds one life-year of progress.';

// IDs of single-line inputs that the mockup shows as textareas
const TEXTAREA_FIELD_IDS = [
	'Transaction_163840', // "We'd love to know more..."
	'Transaction_163841', // "Describe memorial/honor"
];

// ============================================================
// PRESET AMOUNT BUTTONS
// ============================================================

function injectAmountPresets(form) {
	const amountField = form.querySelector('.field.donation-amount');
	const amountInput = form.querySelector('#donation-amount');
	const amountLabel = amountField && amountField.querySelector('label');

	if (!amountField || !amountInput || !amountLabel) return;

	// Inject tagline between the label and the presets
	const tagline = document.createElement('p');
	tagline.className = 'donation-tagline';
	tagline.textContent = DONATION_TAGLINE;
	amountLabel.after(tagline);

	// Build preset buttons container
	const presetsContainer = document.createElement('div');
	presetsContainer.className = 'amount-presets';

	AMOUNT_PRESETS.forEach((preset) => {
		const btn = document.createElement('button');
		btn.type = 'button'; // prevent form submission on click
		btn.className = 'amount-preset-btn';
		btn.textContent = preset.label;
		btn.dataset.amount = preset.value;

		btn.addEventListener('click', () => {
			// Deselect all, then select this one
			presetsContainer
				.querySelectorAll('.amount-preset-btn')
				.forEach((b) => b.classList.remove('is-selected'));
			btn.classList.add('is-selected');

			if (preset.value === 'other') {
				// Show the free-text input for custom amount
				amountInput.classList.remove('donation-amount-hidden');
				amountInput.value = '';
				amountInput.focus();
			} else {
				// Hide the free-text input and set its value for Bloomerang
				amountInput.classList.add('donation-amount-hidden');
				amountInput.value = preset.value;

				// Fire native events so Bloomerang's validation picks up the value
				amountInput.dispatchEvent(
					new Event('input', { bubbles: true })
				);
				amountInput.dispatchEvent(
					new Event('change', { bubbles: true })
				);
				amountInput.dispatchEvent(
					new Event('keyup', { bubbles: true })
				);

				// Also trigger jQuery events since Bloomerang uses jQuery internally
				if (window.jQuery) {
					window
						.jQuery(amountInput)
						.trigger('input')
						.trigger('change')
						.trigger('keyup');
				}
			}
		});

		presetsContainer.appendChild(btn);
	});

	// Insert presets before the amount input, then hide the input
	amountInput.before(presetsContainer);
	amountInput.classList.add('donation-amount-hidden');
}

// ============================================================
// TEXTAREA REPLACEMENTS
// ============================================================

function replaceInputsWithTextareas(form) {
	TEXTAREA_FIELD_IDS.forEach((id) => {
		const input = form.querySelector(`#${id}`);

		// Skip if already a textarea or not found
		if (!input || input.tagName === 'TEXTAREA') return;

		const textarea = document.createElement('textarea');
		textarea.id = input.id;
		textarea.name = input.name;
		textarea.className = input.className;
		textarea.value = input.value;

		if (input.maxLength > 0) {
			textarea.maxLength = input.maxLength;
		}

		input.parentNode.replaceChild(textarea, input);
	});
}

// ============================================================
// URL PRESET SELECTION
// ============================================================

/**
 * Reads ?amount=500 (or ?amount=other) from the URL and clicks the
 * matching preset button so the form arrives pre-filled.
 * For non-preset values (e.g. ?amount=750) it selects "Other" and
 * populates the free-text input with that value.
 */
function applyPresetFromUrl(form) {
	const params = new URLSearchParams(window.location.search);
	const raw = params.get('amount');

	if (!raw) return;

	const normalised = raw.toLowerCase().replace(/,/g, ''); // '1,000' → '1000'
	const presetsContainer = form.querySelector('.amount-presets');

	if (!presetsContainer) return;

	// Find a button whose data-amount matches, else fall back to "Other"
	const matchBtn = presetsContainer.querySelector(
		`[data-amount="${normalised}"]`
	);
	const otherBtn = presetsContainer.querySelector('[data-amount="other"]');
	const targetBtn = matchBtn || otherBtn;

	if (!targetBtn) return;

	targetBtn.click();

	// If we fell back to "Other", populate the free-text input with the raw value
	if (!matchBtn && otherBtn) {
		const amountInput = form.querySelector('#donation-amount');
		if (amountInput) {
			amountInput.value = raw;
			amountInput.dispatchEvent(new Event('input', { bubbles: true }));
			amountInput.dispatchEvent(new Event('change', { bubbles: true }));
			if (window.jQuery) {
				window.jQuery(amountInput).trigger('input').trigger('change');
			}
		}
	}
}

// ============================================================
// ORCHESTRATION
// ============================================================

function enhanceForm(form) {
	injectAmountPresets(form);
	replaceInputsWithTextareas(form);
	applyPresetFromUrl(form);
}

function waitForBloomerangForm(callback) {
	// Form may already be in the DOM if the script loads late
	const existing = document.querySelector('#donation-form');
	if (existing) {
		callback(existing);
		return;
	}

	// Otherwise observe the body for the form to be injected
	const observer = new MutationObserver(() => {
		const form = document.querySelector('#donation-form');
		if (form) {
			observer.disconnect();
			// Small delay so Bloomerang's own init code runs first
			setTimeout(() => callback(form), 100);
		}
	});

	observer.observe(document.body, { childList: true, subtree: true });

	// Safety valve — stop watching after 30 s if form never appears
	setTimeout(() => observer.disconnect(), 30000);
}

// Only initialise when the DOM is ready; the MutationObserver handles
// the async delay of Bloomerang's API call + eval().
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () =>
		waitForBloomerangForm(enhanceForm)
	);
} else {
	waitForBloomerangForm(enhanceForm);
}
